import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { getDirtyFields } from '@ubeya/shared/utils/form';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useSpecificTimeSheet from '@ubeya/shared/hooks/account/useSpecificTimeSheet';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { FlexMiddle, Header2, Header2SemiBold, FormModal as BaseFormModal } from '@ubeya/shared-web/components';
import { EmployeeCard } from 'shared/components';
import { isReadOnly } from 'shared/utils/timesheet';
import UserShift from './UserShift';
import ShiftTable from './ShiftTable';
import BreaksLine from './BreaksLine';
import BreaksTable from './BreaksTable';
import AuditLogsContainer from './AuditLogsContainer';

const FormModal = styled(BaseFormModal)`
  .title {
    padding: 15px 30px;
  }
`;

const Title = styled(Header2)`
  margin-left: 10px;
`;
const SpaceBetween = styled.div`
  margin: 1% 0;
`;

const AdminOnlySection = styled.div`
  margin-top: 20px;
  padding-top: 15px;
  font-size: 14px;
`;

const ShiftDetails = ({
  onClose,
  updateShiftDetails,
  refetchUponUpdate,
  readOnly = false,
  showAuditLogs = true,
  shift,
  shift: {
    id,
    date,
    branchId,
    employee: { id: employeeId, firstName, lastName, image, rating, review },
    employee,
    labelId,
    positionId,
    isJob,
    isExternal,
    timesheetId
  }
}) => {
  const { dateFormat } = useContext(ConfigContext);
  const [isOpen, setIsOpen] = useState(false);
  const { mappedLabels } = useLabels();
  const { mappedPositions } = usePositions();
  const { isUbeyaAdmin } = useUser();

  const { data: specificTimeSheetData } = useSpecificTimeSheet({ timesheetId, isExternal });

  const isRowReadOnly = isReadOnly(shift, readOnly) || (shift?.isJob && shift?.approved);
  const { addResponseError } = useToaster();

  const handleSubmit = useCallback(
    async (values, form) => {
      const { dirtyFields } = form.getState();
      const dirtyValues = getDirtyFields(values, dirtyFields);

      const formattedValues = {
        ...dirtyValues,
        breaktimes: dirtyValues.breaktimes,
        timesheet: { start: { time: dirtyValues.actualIn }, end: { time: dirtyValues.actualOut } }
      };

      try {
        if (id) {
          await updateShiftDetails({ shift, values: formattedValues });
        } else {
          await updateShiftDetails({ shift, values: { ...formattedValues, employeeId, date } });
        }
      } catch (error) {
        addResponseError(error);
        throw error;
      }
      await refetchUponUpdate?.();
      onClose();
    },
    [addResponseError, id, onClose, refetchUponUpdate, updateShiftDetails, shift, employeeId, date]
  );

  const initialValues = {
    ...shift,
    clientApproved: specificTimeSheetData?.clientApproved,
    employeeApproved: specificTimeSheetData?.employeeApproved
  };

  return (
    <>
      <FormModal
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        title={
          <FlexMiddle>
            <Header2SemiBold>{moment(date).format(`ddd, ${dateFormat}`)}</Header2SemiBold>
            <Title>{mappedLabels[labelId]?.title}</Title>
            {labelId && positionId && <Title>-</Title>}
            <Title>{mappedPositions[positionId]?.label}</Title>
          </FlexMiddle>
        }
        confirmText="save"
        showCancelButton>
        {({ values }) => (
          <div>
            <UserShift
              image={image}
              firstName={firstName}
              lastName={lastName}
              review={review}
              rating={rating}
              setEmployee={() => setIsOpen(true)}
              isJob={isJob}
              readOnly={isRowReadOnly}
            />

            <ShiftTable data={values} readOnly={isRowReadOnly} />

            {showAuditLogs && (
              <SpaceBetween>
                <AuditLogsContainer
                  timesheetIds={[timesheetId].filter((timesheetIdValue) => !!timesheetIdValue)}
                  jobIds={[id].filter((idValue) => !!idValue && isJob)}
                  isExternal={isExternal}
                />
              </SpaceBetween>
            )}

            {id && (
              <>
                <BreaksLine startTime={values.actualIn} endTime={values.actualOut} />
                <BreaksTable
                  readOnly={isRowReadOnly}
                  branchId={branchId}
                  date={date}
                  startTime={values.actualIn}
                  endTime={values.actualOut}
                />
              </>
            )}

            {isUbeyaAdmin && timesheetId && <AdminOnlySection>Timesheet ID: {timesheetId}</AdminOnlySection>}
          </div>
        )}
      </FormModal>

      {isOpen && <EmployeeCard defaultData={employee} onClose={() => setIsOpen(false)} />}
    </>
  );
};

export default ShiftDetails;
